import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccountTree as PipelineIcon,
  Launch as LaunchIcon,
  PlaylistAddCheck as ChecklistIcon,
} from '@mui/icons-material';

const INFRA_ACTIONS_URL = 'https://github.com/byai-group/infrastructure/actions';
const BOOTSTRAP_WORKFLOW_URL = 'https://github.com/byai-group/infrastructure/actions/workflows/bootstrap-repo.yaml';
const FLEET_AUDIT_WORKFLOW_URL = 'https://github.com/byai-group/infrastructure/actions/workflows/fleet-deployables-audit.yaml';
const RUNBOOK_URL = 'https://github.com/byai-group/infrastructure/blob/main/docs/NEW_SERVICE_RUNBOOK.md';
const STANDARD_URL = 'https://github.com/byai-group/infrastructure/blob/main/docs/DEPLOYMENT_STANDARD.md';

const repoTypes = [
  {
    type: 'cloud-run-service',
    description: 'HTTP services deployed to Cloud Run through the shared workflow.',
    example: 'Slackbot, OrchestratorAPI, service APIs',
  },
  {
    type: 'python-package',
    description: 'Packages published to Artifact Registry with Python 3.14 build/verify.',
    example: 'byai-platform-endpoints style shared libraries',
  },
  {
    type: 'vercel-app',
    description: 'Frontend apps deployed by native Vercel Git integration.',
    example: 'TripsbyAppUpdated, Tripsby Essentials',
  },
];

export default function CicdSetup() {
  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PipelineIcon color="primary" />
          CI/CD Pipeline Setup
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Use this page when creating, importing, or reviving a repo. The setup lives in GitHub
          so repo onboarding stays close to the place where repos are created.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        GitHub blocks most pages from being embedded in iframes, so this page links directly to
        the approved workflows instead of embedding GitHub.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6">Bootstrap a New Repo</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Creates secrets, variables, environments, workflows, deployables manifest, and a
                    deployment runbook, then opens the onboarding PR.
                  </Typography>
                </Box>
                <Chip label="Requires approval" color="warning" size="small" />
              </Stack>

              <Button
                variant="contained"
                href={BOOTSTRAP_WORKFLOW_URL}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<LaunchIcon />}
                sx={{ mb: 3 }}
              >
                Open Bootstrap Workflow
              </Button>

              <Typography variant="subtitle2" gutterBottom>
                What to enter
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="repo"
                    secondary="The repo name under byai-group, for example Slackbot or Tripsby-Essentials-Apps."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="type"
                    secondary="cloud-run-service, python-package, or vercel-app."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="service_name"
                    secondary="Bare service/package/Vercel project name. Do not add -dev, -staging, or -prod suffixes."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="use_dockerfile"
                    secondary="Set true only for Cloud Run services that build from Dockerfile."
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Repo Types
              </Typography>
              <Stack spacing={2}>
                {repoTypes.map((item) => (
                  <Box key={item.type}>
                    <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                      {item.type}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Example: {item.example}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ChecklistIcon color="primary" />
                New Repo Checklist
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="1. Create the repo in GitHub"
                    secondary="Use main + staging. If an AI creates the repo, it must run bootstrap before calling the task done."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="2. Run Bootstrap repo CI/CD"
                    secondary="The workflow opens a PR into staging when staging exists."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="3. Review and merge the generated PR"
                    secondary="This adds the workflow, deployables.yaml, and docs/DEPLOYMENT.md."
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="4. Validate staging, then promote"
                    secondary="Open staging -> main when the staging deploy or verification is healthy."
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Drift Audit
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use the fleet audit to find repos with undeclared deploy scripts, missing manifests,
                or deferred deployment surfaces.
              </Typography>
              <Button
                variant="outlined"
                href={FLEET_AUDIT_WORKFLOW_URL}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<LaunchIcon />}
              >
                Open Fleet Audit
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" gutterBottom>
        Reference Links
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Link href={INFRA_ACTIONS_URL} target="_blank" rel="noopener noreferrer">
          Infrastructure Actions
        </Link>
        <Link href={RUNBOOK_URL} target="_blank" rel="noopener noreferrer">
          New Service Runbook
        </Link>
        <Link href={STANDARD_URL} target="_blank" rel="noopener noreferrer">
          A+ Deployment Standard
        </Link>
      </Stack>
    </Box>
  );
}
